import * as Tabs from '@wandb/weave/components/Tabs';
import React, {useMemo, useState} from 'react';

import {useMemoizedSelector} from '../../../../state/hooks';
import {useSupportedMediaDrawerTypes} from '../../../PanelMediaBrowser/supportedMediaDrawerTypes';
import {InstrumentedCenteredWaveLoader} from '../../../utility/InstrumentedLoader';
import {useMaybeWorkspaceRefsContext} from '../../../Workspace/WorkspaceRefsContext';
import {SettingsContentWrapper} from '../components/SettingsContentWrapper';
import {useSectionSettingsContextMaybe} from '../SectionSettingsContextProvider';
import {
  useMediaSettingsActions,
  useMediaSettingsContext,
} from './contexts/MediaSettingsContextProvider';
import {MediaSettingsDrawerSection} from './MediaSettingsDrawerSection';
import {makeVisibleMediaTabTitlesSelector} from './mediaSettingsSelectors';
import {SettingsLevel} from './types';
import {getMediaSettingsTabs} from './util';

export const MediaSettingsDrawerContent = ({
  header,
  description,
  settingsLevel,
}: {
  header: React.ReactNode;
  description: string;
  settingsLevel: SettingsLevel;
}) => {
  const {mediaCardTypes, hasMasks, hasBoundingBoxes, rampFlags, loading} =
    useMediaSettingsContext();
  const workspaceRefs = useMaybeWorkspaceRefsContext();
  const sectionRef = useSectionSettingsContextMaybe()?.sectionRef;
  const {panelRef} = useMediaSettingsActions();
  const {types: supportedMediaDrawerTypes} = useSupportedMediaDrawerTypes();
  const staticTabs = useMemo(
    () =>
      getMediaSettingsTabs(
        // Workspace and section scopes can hold types this user's drawer
        // doesn't support, and their settings shouldn't show.
        mediaCardTypes?.filter(mediaCardType =>
          supportedMediaDrawerTypes.includes(mediaCardType)
        ) ?? null,
        hasMasks,
        hasBoundingBoxes,
        settingsLevel,
        rampFlags
      ),
    [
      mediaCardTypes,
      supportedMediaDrawerTypes,
      hasMasks,
      hasBoundingBoxes,
      rampFlags,
      settingsLevel,
    ]
  );
  const visibleTabTitles = useMemoizedSelector(
    makeVisibleMediaTabTitlesSelector,
    staticTabs,
    workspaceRefs?.workspaceSettingsRef,
    sectionRef,
    settingsLevel,
    panelRef
  );
  const tabs = useMemo(
    () => staticTabs.filter(tab => visibleTabTitles.includes(tab.title)),
    [staticTabs, visibleTabTitles]
  );
  const [chosenTab, setChosenTab] = useState<string | undefined>();
  // Tabs depend on the panel's media type, which can load after the drawer
  // opens (a new panel, for instance), so fall back to the first loaded tab.
  const selectedTab = tabs.some(tab => tab.title === chosenTab)
    ? chosenTab
    : tabs[0]?.title;
  // A panel whose key isn't in the sampled history keys gets its type from its
  // loaded media, and its tabs depend on that type.
  if (
    settingsLevel === SettingsLevel.PANEL &&
    mediaCardTypes == null &&
    loading
  ) {
    return (
      <InstrumentedCenteredWaveLoader
        name="media-settings-drawer-type"
        size="huge"
      />
    );
  }
  return (
    <SettingsContentWrapper
      title="Media panel"
      description={description}
      header={header}>
      <Tabs.Root value={selectedTab ?? ''} onValueChange={setChosenTab}>
        <Tabs.List>
          {tabs.map(({title}) => (
            <Tabs.Trigger key={title} data-test={title} value={title}>
              {title}
            </Tabs.Trigger>
          ))}
        </Tabs.List>
        {selectedTab && (
          <Tabs.Content value={selectedTab} tabIndex={-1}>
            <div data-test="media-settings-drawer-content">
              {tabs
                .find(tab => tab.title === selectedTab)
                ?.sections.map(section => (
                  <MediaSettingsDrawerSection
                    key={section.title}
                    {...section}
                  />
                ))}
            </div>
          </Tabs.Content>
        )}
      </Tabs.Root>
    </SettingsContentWrapper>
  );
};
